import { useTranslations } from '@/lib/next-shim'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import { tokens } from '../lib/tokens'
import type { ProfessionalInfo } from '../model/types'
import { DoctorAvatar } from './DoctorAvatar'
import { TrainingSection, ClinicSection } from './ExpandedSections'

interface ExpandedBannerProps {
  info: ProfessionalInfo
  onCollapse: () => void
}

export function ExpandedBanner({ info, onCollapse }: ExpandedBannerProps) {
  const t = useTranslations()
  const { fullName, title, speciality, hasTraining, trainingLabel, trainingDescription, validAddresses } = info
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: tokens.surfaceContainerLowest,
        borderRadius: { xs: 4, md: 6 },
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        border: '1px solid',
        borderColor: `${tokens.outlineVariant}1A`,
      }}
    >
      {/* Decorative background icon */}
      <Box sx={{ position: 'absolute', top: { xs: -20, md: -40 }, right: { xs: -20, md: -40 }, opacity: 0.04, pointerEvents: 'none', zIndex: 0 }}>
        <LocalHospitalIcon sx={{ fontSize: { xs: 200, md: 320 }, color: tokens.primary, transform: 'rotate(12deg)' }} />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 5 } }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={{ xs: 1, md: 3 }}>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <DoctorAvatar size={56} iconSize={28} dotSize={12} />
            </Box>

            <Box>
              <Typography
                fontFamily='"Manrope", sans-serif'
                fontWeight={800}
                fontSize={{ xs: '1.75rem', md: '2.75rem' }}
                color={tokens.primary}
                letterSpacing="-0.04em"
                lineHeight={1.1}
              >
                {fullName}
              </Typography>
              {title && (
                <Typography fontFamily='"Manrope", sans-serif' fontWeight={600} fontSize={{ xs: '1rem', md: '1.25rem' }} color={tokens.secondary} mt={0.5}>
                  {title}
                </Typography>
              )}
              {speciality && (
                <Typography
                  fontFamily='"Manrope", sans-serif'
                  fontWeight={500}
                  fontSize={{ xs: '0.875rem', md: '1.05rem' }}
                  color={tokens.secondary}
                  mt={title ? 0.25 : 0.5}
                >
                  {speciality}
                </Typography>
              )}
            </Box>
          </Stack>

          <IconButton
            onClick={onCollapse}
            sx={{ bgcolor: tokens.surfaceContainerLow, color: tokens.secondary, '&:hover': { bgcolor: tokens.secondaryContainer, color: tokens.primary } }}
          >
            <ExpandLessIcon />
          </IconButton>
        </Stack>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
          gap={{ xs: 3, md: 4 }}
          sx={{ mt: { xs: 3, md: 4 }, pt: { xs: 3, md: 4 }, borderTop: '1px solid', borderColor: `${tokens.outlineVariant}30` }}
        >
          {hasTraining && <TrainingSection heading={t('ProfessionalInfoBanner.training')} label={trainingLabel} description={trainingDescription} />}
          {validAddresses.length > 0 && (
            <ClinicSection heading={t('ProfessionalInfoBanner.clinicLocation')} cabinetLabel={t('ProfessionalCard.cabinet')} addresses={validAddresses} />
          )}
        </Box>
      </Box>
    </Box>
  )
}
